import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode
} from 'react';
import { biometricService } from '../services/biometricService';
import {
  BiometricSupport,
  BiometricSettings,
  BiometricCredential,
  BiometricAuthResult
} from '../types/biometric';
import { useAuth } from './AuthContext';

/** Single capability probe per full page load (shared across Strict Mode remounts). */
let biometricCapabilityPromise: Promise<BiometricSupport> | null = null;

function getBiometricCapabilityOnce(): Promise<BiometricSupport> {
  if (!biometricCapabilityPromise) {
    biometricCapabilityPromise = biometricService.init().catch((error) => {
      biometricCapabilityPromise = null;
      throw error;
    });
  }
  return biometricCapabilityPromise;
}

interface BiometricContextType {
  // Support and availability
  support: BiometricSupport | null;
  isLoading: boolean;
  
  // Settings and credentials
  settings: BiometricSettings | null;
  credentials: BiometricCredential[];
  
  // Actions
  checkSupport: () => Promise<BiometricSupport>;
  registerCredential: (nickname?: string) => Promise<BiometricCredential>;
  authenticate: (credentialId?: string, signal?: AbortSignal) => Promise<BiometricAuthResult>;
  authenticateConditional: (signal?: AbortSignal) => Promise<BiometricAuthResult>;
  deleteCredential: (credentialId: string) => Promise<void>;
  updateCredentialNickname: (credentialId: string, nickname: string) => Promise<void>;
  updateSettings: (settings: Partial<BiometricSettings>) => Promise<void>;
  refreshSettings: () => Promise<void>;
  
  // UI state
  showBiometricPrompt: boolean;
  setShowBiometricPrompt: (show: boolean) => void;
  biometricError: string | null;
  setBiometricError: (error: string | null) => void;
}

const BiometricContext = createContext<BiometricContextType | undefined>(undefined);

export const BiometricProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [support, setSupport] = useState<BiometricSupport | null>(null);
  const [settings, setSettings] = useState<BiometricSettings | null>(null);
  const [credentials, setCredentials] = useState<BiometricCredential[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [biometricError, setBiometricError] = useState<string | null>(null);

  // Device capability only, once per page load (no server settings).
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getBiometricCapabilityOnce()
      .then((supportResult) => {
        if (!cancelled) {
          setSupport(supportResult);
        }
      })
      .catch((error) => {
        console.error('Failed to initialize biometric service:', error);
        if (!cancelled) {
          setSupport({
            isSupported: false,
            isAvailable: false,
            supportedAuthenticators: [],
            error: error instanceof Error ? error.message : 'Failed to initialize biometric service'
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load biometric settings from backend (call from login/settings UI only).
  const loadSettings = useCallback(async () => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      console.log('No auth token, skipping biometric settings load');
      setSettings({
        enabled: false,
        requireForLogin: false,
        requireForTransactions: false,
        credentials: []
      });
      setCredentials([]);
      return;
    }

    try {
      const biometricSettings = await biometricService.getBiometricSettings();
      setSettings(biometricSettings);
      setCredentials(biometricSettings.credentials || []);
    } catch (error) {
      console.error('Failed to load biometric settings:', error);
      setSettings({
        enabled: false,
        requireForLogin: false,
        requireForTransactions: false,
        credentials: []
      });
      setCredentials([]);
    }
  }, []);

  // Check biometric support
  const checkSupport = async (): Promise<BiometricSupport> => {
    try {
      const supportResult = await biometricService.getSupport();
      setSupport(supportResult);
      return supportResult;
    } catch (error) {
      const errorResult = {
        isSupported: false,
        isAvailable: false,
        supportedAuthenticators: [],
        error: error instanceof Error ? error.message : 'Failed to check biometric support'
      } as BiometricSupport;
      setSupport(errorResult);
      return errorResult;
    }
  };

  // Register a new biometric credential
  const registerCredential = async (nickname?: string): Promise<BiometricCredential> => {
    try {
      setBiometricError(null);
      
      // Prepare user information for credential creation
      const userInfo = user ? {
        username: user.username,
        email: user.emails?.[0]?.address,
        displayName: user.profile?.firstName && user.profile?.lastName 
          ? `${user.profile.firstName} ${user.profile.lastName}`
          : user.username
      } : undefined;
      
      const credential = await biometricService.registerCredential(nickname, userInfo);
      
      // Refresh settings to get updated credentials list
      await loadSettings();
      
      return credential;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to register biometric credential';
      setBiometricError(errorMessage);
      throw error;
    }
  };

  // Authenticate using biometric
  const authenticate = async (credentialId?: string, signal?: AbortSignal, username?: string): Promise<BiometricAuthResult> => {
    setBiometricError(null);
    
    const result = await biometricService.authenticate(credentialId, signal, username);
    
    if (!result.success) {
      setBiometricError(result.error || 'Authentication failed');
    }
    
    return result;
  };

  // Authenticate using conditional UI (autofill)
  const authenticateConditional = async (signal?: AbortSignal, username?: string): Promise<BiometricAuthResult> => {
    setBiometricError(null);
    
    const result = await biometricService.authenticateConditional(signal, username);
    
    if (!result.success) {
      setBiometricError(result.error || 'Authentication failed');
    }
    
    return result;
  };

  // Delete a biometric credential
  const deleteCredential = async (credentialId: string): Promise<void> => {
    try {
      setBiometricError(null);
      await biometricService.deleteCredential(credentialId);
      
      // Refresh settings to get updated credentials list
      await loadSettings();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete biometric credential';
      setBiometricError(errorMessage);
      throw error;
    }
  };

  // Update credential nickname
  const updateCredentialNickname = async (credentialId: string, nickname: string): Promise<void> => {
    try {
      setBiometricError(null);
      await biometricService.updateCredentialNickname(credentialId, nickname);
      
      // Refresh settings to get updated credentials list
      await loadSettings();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update credential nickname';
      setBiometricError(errorMessage);
      throw error;
    }
  };

  // Update biometric settings
  const updateSettings = async (newSettings: Partial<BiometricSettings>): Promise<void> => {
    try {
      setBiometricError(null);
      await biometricService.updateBiometricSettings(newSettings);
      
      // Update local settings
      setSettings(prev => prev ? { ...prev, ...newSettings } : null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update biometric settings';
      setBiometricError(errorMessage);
      throw error;
    }
  };

  const refreshSettings = useCallback(async (): Promise<void> => {
    await loadSettings();
  }, [loadSettings]);

  const value: BiometricContextType = {
    // Support and availability
    support,
    isLoading,
    
    // Settings and credentials
    settings,
    credentials,
    
    // Actions
    checkSupport,
    registerCredential,
    authenticate,
    authenticateConditional,
    deleteCredential,
    updateCredentialNickname,
    updateSettings,
    refreshSettings,
    
    // UI state
    showBiometricPrompt,
    setShowBiometricPrompt,
    biometricError,
    setBiometricError
  };

  return (
    <BiometricContext.Provider value={value}>
      {children}
    </BiometricContext.Provider>
  );
};

export const useBiometric = (): BiometricContextType => {
  const context = useContext(BiometricContext);
  if (context === undefined) {
    throw new Error('useBiometric must be used within a BiometricProvider');
  }
  return context;
};

export default BiometricContext; 